/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEvent, useCallback, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import isNil from 'lodash.isnil';
import {AppLink} from '@ion/applications/src/link/AppLink';
import {showPendingJobs} from '@ion/applications/src/pages/devices/state';
import {translate} from '@ion/core/src/locale/locale';
import {DownloadJobType} from '@ion/core/src/types/entities';
import {getQueryString} from '@ion/core/src/utils/ObjectUtil';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import {dlsColor, dlsZIndex} from '@ion/dls/src/theme/theme';
import {Notification} from '@ion/dls/src/types/extensions';
import {DLSTheme} from '@ion/dls/src/types/types';
import useEnvQueryParam from '../hooks/useEnvQueryParam';
import useJobSearchRerun from '../hooks/useJobSearchRerun';
import {isJobNotification, jobNotificationJobId, setNotificationsPanelStatus} from './state';

export interface ViewEventCtaProps {
  notification?: Notification;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    viewEventCta : {
      marginLeft : '8px',
      padding    : '4px 4px',
      fontSize   : '14px',
      '& a'      : {
        color          : dlsColor('aaBlue'),
        textDecoration : 'none',
        '&:hover'      : {
          textDecoration : 'underline'
        },
        '&:active, &:focus' : {
          color               : dlsColor('grey1'),
          textDecoration      : 'underline',
          textDecorationColor : dlsColor('grey1')
        }
      }
    }
  };
});

/**
 * "View Event" deep link CTA component suitable for passing to the DLS
 * NotificationCard component via its viewEventCta prop. This component
 * CTA exists separately from the NotificationCard component because the
 * NotificationCard component is in the DLS package, and this component
 * incorporates the AppLink component which is in the applications package.
 * Since the applications package depends on the DLS package, if this
 * component were part of the NotificationCard in the DLS, it would create
 * a circular dependency.
 *
 * ```typescript
 *
 *  import ViewEventCta from './ViewEventCta';
 *
 *  <NotificationCard
 *    notification={notification}
 *    viewEventCta={<ViewEventCta notification={notification} />}
 *    ... />
 *
 * ```
 *
 */
export const ViewEventCta = ({
  notification
}: ViewEventCtaProps) => {
  const classes  = useStyles();
  const dispatch = useDispatch();
  const env      = useEnvQueryParam();
  const {assetId, createdOn, avlMessageUuid, jobStatus} = notification;
  const isValid  = !isNil(assetId) && !isNil(createdOn) && !isNil(avlMessageUuid);
  const isFailed = jobStatus === DownloadJobType.Failed;

  const {jobRerunLoading, reRunJobSelected, rerunJob} = useJobSearchRerun();
  const [rerunStarted, setRerunStarted] = useState(false);

  useEffect(() => {
    if (reRunJobSelected && !jobRerunLoading) {
      setRerunStarted(true);
    }
  }, [reRunJobSelected, jobRerunLoading]);

  const onViewJob = useCallback(function(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    dispatch(setNotificationsPanelStatus(false));
    dispatch(showPendingJobs({jobId: jobNotificationJobId(notification?.uuid)}, true));
  }, [notification]);

  const onRerunJob = useCallback(function(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    rerunJob({jobId: jobNotificationJobId(notification?.uuid)});
  }, [notification]);

  const onRerunToastClose = useCallback(function() {
    setRerunStarted(false);
  }, []);

  if (isJobNotification(notification)) {
    return (
      <>
        <TertiaryButton className={classes.viewEventCta} onClick={onViewJob}>
          {translate('notifications:panel.viewJob')}
        </TertiaryButton>
        {isFailed && (
          <TertiaryButton className={classes.viewEventCta}
            onClick={onRerunJob}
            disabled={jobRerunLoading}>
            {translate('notifications:panel.rerunJob')}
          </TertiaryButton>
        )}
        {rerunStarted && (
          <IonSnackbar show
            zIndex={dlsZIndex('savedColumnSnackbar')}
            message={translate('notifications:panel.rerunSuccess')}
            actionLabel={translate('notifications:panel.rerunOk')}
            onAction={onRerunToastClose}
            onClose={onRerunToastClose}
            autoHideDuration={3000} />
        )}
      </>
    );
  }
  if (isValid) {
    const href = `/tracking?${getQueryString({
      ...env,
      report  : 'assetHistory',
      eventId : avlMessageUuid,
      date    : createdOn,
      assetId
    })}`;
    return (
      <TertiaryButton className={classes.viewEventCta}>
        <AppLink href={href}>
          {translate('notifications:panel.viewEvent')}
        </AppLink>
      </TertiaryButton>
    );
  }
  return null;
};

export default ViewEventCta;
